// GenericTooltip.tsx
import React, { useLayoutEffect, useRef, useState } from "react";
import { ReactComponent as IconInfo } from "../../shared/assets/Icons/Icon_Question.svg";
import { createPortal } from "react-dom";

export interface GenericTooltipProps {
    /** Texto que se mostrará dentro del tooltip. */
    text: string;
    /**
     * Posición del tooltip relativa al ícono
     * @default "top"
     */
    position?: "top" | "bottom" | "left" | "right";
    /**
     * Icono personalizado que se mostrará en lugar del predeterminado.
     */
    icon?: React.ReactNode;
    children?: React.ReactNode;
    /**
     * Ancho personalizado del tooltip
     * @default "16rem"
     */
    width?: string;
    /**
     * Estilos personalizados para casos específicos
     */
    customStyle?: React.CSSProperties;
    /**
     * Offset personalizado para posicionamiento del tooltip
     * @default undefined (usa el posicionamiento automático)
     */
    customOffset?: {
        top?: number;
        left?: number;
        right?: number;
        bottom?: number;
    };
    /**
     * Controla si el tooltip es visible o no
     * @default undefined (se controla automáticamente con hover)
     */
    isVisible?: boolean;
    /**
     * Clase CSS personalizada para el ancho del span trigger
     * @default undefined (usa el ancho por defecto)
     */
    triggerWidthClass?: string;
}

/**
 * Componente genérico de tooltip:
 * Muestra un ícono pequeño y despliega un pop-up con el texto al hacer hover.
 * Usamos las variables de tu theme (light/dark) para los colores.
 */
const GenericTooltip: React.FC<GenericTooltipProps> = ({
    text,
    position = "top",
    icon,
    children,
    width = "16rem",
    customStyle,
    customOffset,
    isVisible,
    triggerWidthClass,
}) => {
    const [hoverCount, setHoverCount] = useState(0);
    const [rect, setRect] = useState<DOMRect>();
    const triggerRef            = useRef<HTMLSpanElement>(null);

    // 2) Compute portal styles based on `position`
    const style: React.CSSProperties = {
        position: "fixed",
        zIndex: 9999,
        maxWidth: width,
        cursor: "pointer",
        ...customStyle, // Merge custom styles, allowing them to override defaults
    };

    // Clases CSS para posicionar el tooltip según la prop "position"
    if (rect) {
        const gap = 8;
        
        // Apply custom offset first if provided
        let offsetLeft = 0;
        let offsetTop = 0;
        if (customOffset) {
          offsetLeft = customOffset.left || 0;
          offsetTop = customOffset.top || 0;
          if (customOffset.right !== undefined) {
            offsetLeft = -customOffset.right;
          }
          if (customOffset.bottom !== undefined) {
            offsetTop = -customOffset.bottom;
          }
        }
        
        switch (position) {
          case "top":
            style.top = rect.top - gap + offsetTop;
            style.left = rect.left + rect.width / 2 + offsetLeft;
            style.transform = "translate(-50%, -100%)";
            break;
          case "bottom":
            style.top = rect.bottom + gap + offsetTop;
            style.left = rect.left + rect.width / 2 + offsetLeft;
            style.transform = "translate(-50%, 0)";
            break;
          case "left":
            style.top = rect.top + rect.height / 2 + offsetTop;
            style.left = rect.left - gap + offsetLeft;
            style.transform = "translate(-100%, -50%)";
            break;
          case "right":
            style.top = rect.top + rect.height / 2 + offsetTop;
            style.left = rect.right + gap + offsetLeft;
            style.transform = "translate(0, -50%)";
            break;
        }
      }

    const trigger = children ?? (
        <span className={`inline-flex items-center cursor-pointer text-semantic-text-secondary ${triggerWidthClass || ''}`}>
          {icon ?? <IconInfo className="w-5 h-5 text-current" />}
        </span>
    );

  // Re-measure trigger before paint when showing
  useLayoutEffect(() => {
    if (hoverCount > 0 && triggerRef.current) {
      setRect(triggerRef.current.getBoundingClientRect());
    }
  }, [hoverCount]);


  const shouldShowTooltip = (isVisible !== undefined ? isVisible : hoverCount > 0) && text && text.trim().length > 0;

    // Handlers to bump the counter up/down
    const handleEnter = () => setHoverCount(c => c + 1);
    const handleLeave = () => setHoverCount(c => Math.max(0, c - 1));


    return (
        <>
            {/* Ícono que, al hacer hover, muestra el tooltip */}
            <span
                ref={triggerRef}
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
                className={`inline-flex items-center cursor-pointer text-semantic-text-secondary ${triggerWidthClass || ''}`}
            >
                {/* If 'icon' is provided, render it; otherwise, show default IconInfo */}
                {trigger}
            </span>

            {/* Tooltip con estilos del theme */}
            {shouldShowTooltip &&
            createPortal(
          <div
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            style={style}
            className="
              px-2 py-1 text-xs
              text-semantic-text-primary
              bg-semantic-bg-secondary
              border border-semantic-border-secondary
              rounded shadow-md
            "
          >
            {text}
          </div>,
          document.body
        )}
        </>
    );
};

export default GenericTooltip;
